import type { FieldDefinition, FieldId } from '@inside-us-aos/domain';

/**
 * Phase 1.1 — default PII redaction set computation.
 *
 * The audit emitter accepts an optional `redactedFields:
 * ReadonlySet<FieldId>` and substitutes a sentinel value for raw
 * `oldValue` / `newValue` payloads on those fields. This module
 * derives a default set from the catalog by **field-id pattern
 * matching**.
 *
 * Why pattern matching (not a `pii: true` catalog flag): the
 * catalog is a 580-field surface area shared with the screener and
 * PDF engine. Adding a new flag is a high-stakes change (Phase 5
 * may add it; Phase 1 review explicitly recommends not touching
 * the catalog now). The pattern set below is conservative —
 * **err on the side of redacting** — and centralizes the policy
 * so Phase 5 can replace it with a catalog-flag-based set without
 * touching any other module.
 *
 * ### Redacted patterns (any catalog field id matching ≥1 pattern
 * is redacted)
 *
 * | Pattern (substring or suffix) | Reason |
 * |---|---|
 * | `.ssn`                        | Social Security number |
 * | `.alien_registration_number`  | A-number |
 * | `alien_number`                | A-number alt naming |
 * | `.birth.date`                 | Date of birth |
 * | `birth_date`                  | DOB alt naming |
 * | `.i94.number`                 | I-94 record number |
 * | `.visa.number`                | Visa number |
 * | `.passport_number`            | Passport number |
 * | `mailing_address.`            | Home / mailing address parts |
 * | `physical_address.`           | Physical address parts |
 * | `address_history.`            | Address history rows |
 * | `marriage.place.`             | Marriage location (PII-adjacent) |
 * | `prior_marriages.`            | Prior-marriage history |
 * | `tax_history.`                | Tax-year financial history |
 * | `receipt_number`              | USCIS receipt numbers |
 *
 * The patterns are **substrings** matched case-sensitively against
 * the field id. We deliberately keep the list narrow and explicit;
 * a `.address` substring would over-match (e.g. an enum value
 * named `address` could appear in a non-PII id, though none do
 * today). Audit-time false positives (over-redaction) are
 * preferable to false negatives.
 *
 * The function returns a `ReadonlySet<FieldId>` containing every
 * catalog id matched by any pattern. Callers pass this directly to
 * `createAuditEmitter(store, { redactedFields })`.
 */

const PII_FIELD_PATTERNS: readonly string[] = [
  '.ssn',
  '.alien_registration_number',
  'alien_number',
  '.birth.date',
  'birth_date',
  '.i94.number',
  '.visa.number',
  '.passport_number',
  'mailing_address.',
  'physical_address.',
  'address_history.',
  'marriage.place.',
  'prior_marriages.',
  'tax_history.',
  'receipt_number',
];

const matchesPiiPattern = (fieldId: string): boolean => {
  for (const pattern of PII_FIELD_PATTERNS) {
    if (fieldId.includes(pattern)) return true;
  }
  return false;
};

/**
 * Computes the default set of field ids whose audit-event payloads
 * must be redacted. Iterates the catalog once and tests each id
 * against the pattern list. Stable per-catalog reference.
 */
export const computeDefaultPiiRedactionSet = (
  catalog: readonly FieldDefinition[],
): ReadonlySet<FieldId> => {
  const set = new Set<FieldId>();
  for (const field of catalog) {
    if (matchesPiiPattern(field.id)) {
      set.add(field.id);
    }
  }
  return set;
};

/**
 * Exported for tests and Phase 5 audits. The patterns themselves
 * are part of the redaction contract — changes here change which
 * fields leak vs. redact.
 */
export const PII_REDACTION_PATTERNS = PII_FIELD_PATTERNS;
